import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '../App';
import { formatCurrency } from '../components/Charts';

function normalize(text) {
  return String(text || '').trim().toLowerCase();
}

function buildAnswer(question, context) {
  const q = normalize(question);
  const {
    kpiSavings,
    kpiSpend,
    kpiOpen,
    kpiAccepted,
    cloudAccounts,
    riskItems,
    categoryItems,
    topActions,
  } = context;

  const topRisk = [...riskItems].sort((a, b) => Number(b.count || 0) - Number(a.count || 0))[0];
  const topCategory = [...categoryItems].sort((a, b) => Number(b.count || 0) - Number(a.count || 0))[0];
  const executionBase = kpiOpen + kpiAccepted;
  const executionRate = executionBase > 0 ? Math.round((kpiAccepted / executionBase) * 100) : 0;

  if (q.includes('saving') || q.includes('opportunity') || q.includes('spend')) {
    const savingsPct = kpiSpend > 0 ? Math.round((kpiSavings / kpiSpend) * 100) : 0;
    return [
      `Current monthly spend is ${kpiSpend > 0 ? formatCurrency(kpiSpend) : 'not available yet'}.`,
      `Open opportunity is ${kpiSavings > 0 ? formatCurrency(kpiSavings) : '0'} per month${kpiSpend > 0 ? ` (${savingsPct}% of spend)` : ''}.`,
      'Suggested next step: review top recommendations and accept low-risk items first.',
    ].join(' ');
  }

  if (q.includes('risk')) {
    if (!topRisk) {
      return 'Risk distribution is not available yet. Run sync on connected accounts to populate risk insights.';
    }
    return `Primary risk concentration is ${topRisk.bucket || topRisk.label} with ${topRisk.count} recommendations. Suggested next step: focus on high-value items in Recommendations.`;
  }

  if (q.includes('account') || q.includes('fleet') || q.includes('cloud')) {
    return `There are ${Number(cloudAccounts?.length || 0).toLocaleString()} connected accounts. Suggested next step: run Sync now for any recently changed credentials and review account health on Dashboard.`;
  }

  if (q.includes('execute') || q.includes('action') || q.includes('backlog')) {
    return `Execution rate is ${executionBase > 0 ? `${executionRate}%` : 'not available yet'} with ${Number(kpiOpen || 0).toLocaleString()} open and ${Number(kpiAccepted || 0).toLocaleString()} accepted recommendations. Suggested next step: convert top 3 open actions this week.`;
  }

  if (q.includes('category') || q.includes('mix')) {
    if (!topCategory) {
      return 'Category mix is not available yet. Run account sync to build recommendation categories.';
    }
    return `Top optimization category is ${topCategory.category || topCategory.label} with ${topCategory.count} items. Suggested next step: create a focused execution sprint for this category.`;
  }

  if (q.includes('what should i do') || q.includes('what next') || q.includes('next')) {
    return `Start with ${Number(topActions?.length || 0)} top-priority findings, then reduce open backlog from ${Number(kpiOpen || 0).toLocaleString()} by accepting low-risk recommendations first.`;
  }

  return 'Ask about savings, risk, accounts, backlog, or what to do next. I answer using your live dashboard data.';
}

export default function AssistantPage() {
  const {
    kpiSavings,
    kpiSpend,
    kpiOpen,
    kpiAccepted,
    cloudAccounts,
    riskItems,
    categoryItems,
    topActions,
  } = useApp();

  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      text: 'I can answer questions about your savings, risk, account posture, and execution backlog.',
    },
  ]);

  const context = useMemo(() => ({
    kpiSavings,
    kpiSpend,
    kpiOpen,
    kpiAccepted,
    cloudAccounts,
    riskItems,
    categoryItems,
    topActions,
  }), [kpiSavings, kpiSpend, kpiOpen, kpiAccepted, cloudAccounts, riskItems, categoryItems, topActions]);

  function ask(question) {
    const trimmed = String(question || '').trim();
    if (!trimmed) return;
    const answer = buildAnswer(trimmed, context);
    setMessages((prev) => ([
      ...prev,
      { role: 'user', text: trimmed },
      { role: 'assistant', text: answer },
    ]));
    setInput('');
  }

  return (
    <div className="page-view assistant-page">
      <div className="page-header">
        <p className="feature-eyebrow">Assistant</p>
        <h2>Ask SROIK</h2>
        <p className="page-subtitle">Quick answers from your current dashboard data.</p>
      </div>

      <div className="workspace-block">
        <div className="assistant-quick-asks">
          <button className="secondary-action" type="button" onClick={() => ask('How much can we save this month?')}>Savings summary</button>
          <button className="secondary-action" type="button" onClick={() => ask('Where is our biggest risk right now?')}>Top risk</button>
          <button className="secondary-action" type="button" onClick={() => ask('What should we do next?')}>What next</button>
          <button className="secondary-action" type="button" onClick={() => ask('How is execution going?')}>Execution health</button>
        </div>

        <div className="assistant-thread">
          {messages.map((message, index) => (
            <div key={`${message.role}-${index}`} className={message.role === 'user' ? 'assistant-msg user' : 'assistant-msg bot'}>
              <p>{message.text}</p>
            </div>
          ))}
        </div>

        <div className="assistant-composer">
          <input
            type="text"
            value={input}
            onChange={(event) => setInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                ask(input);
              }
            }}
            placeholder="Ask about savings, risk, accounts, or backlog"
          />
          <button className="primary-action" type="button" onClick={() => ask(input)}>Ask</button>
        </div>

        <p className="assistant-footnote">
          Need human support? Open <Link to="/app/website-inbox">Website Inbox</Link> for direct responses.
        </p>
      </div>
    </div>
  );
}
